// Método Húngaro para el problema de asignación, con registro paso a paso.

export interface HungarianStep {
  title: string;
  description: string;
  matrix: number[][]; // matriz de costos reducida actual
  coveredRows?: boolean[];
  coveredCols?: boolean[];
  assignment?: { row: number; col: number }[]; // asignación resaltada (ceros marcados)
}

export interface HungarianResult {
  steps: HungarianStep[];
  assignment: { row: number; col: number }[]; // fila -> columna (índices del problema padded)
  size: number; // tamaño cuadrado usado (con relleno)
  origRows: number;
  origCols: number;
}

const clone = (m: number[][]) => m.map((r) => r.slice());

// Emparejamiento máximo bipartito (Kuhn) usando aristas en los ceros.
function maxMatching(zero: boolean[][], n: number): number[] {
  const matchCol = new Array(n).fill(-1); // columna j -> fila asignada
  const tryRow = (r: number, seen: boolean[]): boolean => {
    for (let c = 0; c < n; c++) {
      if (zero[r][c] && !seen[c]) {
        seen[c] = true;
        if (matchCol[c] === -1 || tryRow(matchCol[c], seen)) {
          matchCol[c] = r;
          return true;
        }
      }
    }
    return false;
  };
  for (let r = 0; r < n; r++) tryRow(r, new Array(n).fill(false));
  return matchCol;
}

// Cobertura mínima de líneas (teorema de König) a partir del emparejamiento máximo.
function minLineCover(
  zero: boolean[][],
  n: number
): { coveredRows: boolean[]; coveredCols: boolean[]; lines: number; matchCol: number[] } {
  const matchCol = maxMatching(zero, n);
  const matchRow = new Array(n).fill(-1);
  for (let c = 0; c < n; c++) if (matchCol[c] !== -1) matchRow[matchCol[c]] = c;

  const markedRow = new Array(n).fill(false);
  const markedCol = new Array(n).fill(false);

  // Marcar filas sin asignar.
  const queue: number[] = [];
  for (let r = 0; r < n; r++)
    if (matchRow[r] === -1) {
      markedRow[r] = true;
      queue.push(r);
    }

  // Propagar marcas alternando por ceros y por el emparejamiento.
  while (queue.length) {
    const r = queue.shift() as number;
    for (let c = 0; c < n; c++) {
      if (zero[r][c] && !markedCol[c]) {
        markedCol[c] = true;
        const r2 = matchCol[c];
        if (r2 !== -1 && !markedRow[r2]) {
          markedRow[r2] = true;
          queue.push(r2);
        }
      }
    }
  }

  // Cobertura = filas NO marcadas + columnas marcadas.
  const coveredRows = markedRow.map((v) => !v);
  const coveredCols = markedCol.slice();
  const lines =
    coveredRows.filter(Boolean).length + coveredCols.filter(Boolean).length;
  return { coveredRows, coveredCols, lines, matchCol };
}

// Resuelve el problema de asignación. `costs` ya viene transformado
// (penalización si es maximización, BIG en rutas prohibidas).
export function hungarian(costs: number[][]): HungarianResult {
  const origRows = costs.length;
  const origCols = costs[0].length;
  const n = Math.max(origRows, origCols);

  // Rellenar a matriz cuadrada con ceros (filas/columnas ficticias).
  const M: number[][] = [];
  for (let i = 0; i < n; i++) {
    M.push([]);
    for (let j = 0; j < n; j++) {
      M[i].push(i < origRows && j < origCols ? costs[i][j] : 0);
    }
  }

  const steps: HungarianStep[] = [];
  steps.push({
    title: 'Matriz inicial',
    description:
      n !== origRows || n !== origCols
        ? `La matriz no es cuadrada; se rellena a ${n}×${n} con filas/columnas ficticias (costo 0).`
        : 'Partimos de la matriz de costos.',
    matrix: clone(M),
  });

  // Reducción por filas.
  for (let i = 0; i < n; i++) {
    const min = Math.min(...M[i]);
    if (min > 0) for (let j = 0; j < n; j++) M[i][j] -= min;
  }
  steps.push({
    title: 'Reducción por filas',
    description: 'A cada fila se le resta su valor mínimo, generando al menos un cero por fila.',
    matrix: clone(M),
  });

  // Reducción por columnas.
  for (let j = 0; j < n; j++) {
    let min = Infinity;
    for (let i = 0; i < n; i++) min = Math.min(min, M[i][j]);
    if (min > 0) for (let i = 0; i < n; i++) M[i][j] -= min;
  }
  steps.push({
    title: 'Reducción por columnas',
    description: 'A cada columna se le resta su valor mínimo, generando al menos un cero por columna.',
    matrix: clone(M),
  });

  let guard = 0;
  while (guard++ < 200) {
    const zero = M.map((row) => row.map((x) => x === 0));
    const { coveredRows, coveredCols, lines, matchCol } = minLineCover(zero, n);

    if (lines >= n) {
      // Óptimo: el emparejamiento de ceros es una asignación completa.
      const assignment: { row: number; col: number }[] = [];
      for (let c = 0; c < n; c++) if (matchCol[c] !== -1) assignment.push({ row: matchCol[c], col: c });
      steps.push({
        title: 'Asignación óptima',
        description: `Se cubren todos los ceros con ${lines} líneas (= n). Se selecciona un cero por fila y columna: asignación óptima encontrada.`,
        matrix: clone(M),
        assignment,
      });
      return { steps, assignment, size: n, origRows, origCols };
    }

    // Mostrar la cobertura actual.
    steps.push({
      title: `Cobertura de ceros (${lines} líneas)`,
      description: `Se cubren todos los ceros con ${lines} líneas, que es menos que n = ${n}. Hay que crear más ceros.`,
      matrix: clone(M),
      coveredRows,
      coveredCols,
    });

    // Mínimo no cubierto.
    let e = Infinity;
    for (let i = 0; i < n; i++)
      for (let j = 0; j < n; j++)
        if (!coveredRows[i] && !coveredCols[j]) e = Math.min(e, M[i][j]);

    // Restar e a los no cubiertos, sumar e a los doblemente cubiertos.
    for (let i = 0; i < n; i++)
      for (let j = 0; j < n; j++) {
        if (!coveredRows[i] && !coveredCols[j]) M[i][j] -= e;
        else if (coveredRows[i] && coveredCols[j]) M[i][j] += e;
      }

    steps.push({
      title: `Ajuste (mínimo no cubierto = ${e})`,
      description: `Se resta ${e} a los elementos no cubiertos y se suma ${e} a los cubiertos por dos líneas. Se repite el conteo de líneas.`,
      matrix: clone(M),
    });
  }

  // Salvaguarda (no debería llegar aquí).
  return { steps, assignment: [], size: n, origRows, origCols };
}
